import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { Alert, Card } from "@mantine/core";
import { SHIFT_ROSTER_TICK } from "../../../data/api/shiftsApi";
import { IState } from "../../../data/reducers/state";
import { IGame } from "../../../types/game/Game";
import { IPerson } from "../../../types/persons/person";
import { IGamePlaceholder } from "../../../types/shifts/Shift";
import { IShiftEnrolment } from "../../../types/shifts/ShiftEnrolment";
import { IVenue } from "../../../types/venue/Venue";
import RosterAdministratorCollapse from "./admin/RosterAdministratorCollapse";
import RefereesBreakCell from "./RefereesBreakCell";
import RosterGameCell from "./RosterGameCell";
import RosterSupervisorCollapse from "./supervisor/RosterSupervisorCollapse";

const styles = {
  venueContainer: {
    padding: "10 px",
  },
  column: {
    padding: "10px",
    float: "left",
  },
  td: {
    border: "1px solid black",
    backgroundColor: "theme ? theme.palette.background.paper : white",
    verticalAlign: "top",
    padding: "5px",
  },
  nogameBorder: {
    borderLeft: "1px solid black",
    borderRight: "1px solid black",
    borderTop: "0",
    borderBottom: "0",
    backgroundColor: "var(--color-bg-disabled-card)",
  },
  timeCell: {
    borderLeft: "1px solid black",
    borderRight: "1px solid black",
    borderTop: "0",
    borderBottom: "0",
    verticalAlign: "top",
    paddingTop: "5px",
  },
  th: {
    border: "1px solid black",
    padding: "10px",
  },
};

interface IRosterGridProps {
  shiftId: string;
  sessionId: string;
  officials: IShiftEnrolment[];
  hideTeamNames: boolean;
  gridView: string;
  showExtraPadding: boolean;
  showHideAppointmentFromSheetButton: boolean;

  slots: [
    DateTime, // slot
    { [key: string]: React.ReactNode | IGamePlaceholder | null },
  ][];

  personsOnShift: string[];
  personsMap: { [key: string]: IPerson };
  gamesMap: { [key: string]: IGame };

  venues: IVenue[];

  highlightProblemGames: boolean;
}

const RosterGrid: React.FunctionComponent<IRosterGridProps> = ({
  shiftId,
  sessionId,
  venues,
  slots,
  gamesMap,
  hideTeamNames,
  highlightProblemGames,
  gridView,
  personsOnShift,
  showExtraPadding,
  ...props
}) => {
  const venueCourts: { vid: string; court: string }[] = [];
  for (const venue of venues) {
    venueCourts.push(
      ...venue.courts.map((crt) => ({ vid: venue._id, court: crt })),
    );
  }
  const width = `${Math.trunc(99 / venueCourts.length)}%`;

  /*
    Refs on break:

      Get a list of all referees at this venue who are officiating, and then check their availabilities
      to see if they're available for the length of the slot.

      We'll decide the slot if there is at least one game that starts on that slot, then we'll set "break": null
      on each of the covered slots, so a nogame cell can be bound on the other ones
  */

  const tick = SHIFT_ROSTER_TICK;
  const widthRequiredPerCourt = 160 / venueCourts.length + 5;
  const breakCellLength = 45; // maybe choose something more intelligent later
  const startstart = slots?.[0]?.[0];
  const breakCellMap = {};
  if (startstart) {
    let i = 0;
    while (i < slots.length) {
      const courtsInSlot = slots?.[i]?.[1];
      const courtsWithGames = Object.keys(courtsInSlot).filter(
        (c) => !!courtsInSlot[c],
      );
      if (courtsWithGames.length === 0) {
        i++;
        continue;
      }
      const start = startstart.plus({ minutes: i * tick });
      // console.log("Adding break cell to " + start.format("hh:mm a"))
      // @ts-ignore - it's definitely an IGamePlaceholder
      const game: IGamePlaceholder = courtsInSlot[courtsWithGames[0]];
      // @ts-ignore
      breakCellMap[i] = {
        start,
        finish: start.plus({ minutes: game.length * tick }),
        span: game.length,
      };
      i += game.length || 1;
    }
  }

  // construct some table where there is a row for every five minutes (truncate all time this 5 minutes)
  // then use the .gameLength property and use rowspan={length/5} to correctly size cells
  // note, don't return any cell in a table when there is a game there,
  // we might need to construct a different data source to faciliate this 5 minute blocking
  const gameCellWidth = `calc(${width} - ${widthRequiredPerCourt}px)`;

  const timezones = new Set(venues.map((v) => v.tz));
  const computerTimezone = DateTime.local().zoneName;
  let tzWarning: string | null = null;
  if (timezones.size > 1) {
    tzWarning = "Venues span multiple timezones: " + Array.from(timezones).join(", ");
  }
  if (!computerTimezone || !timezones.has(computerTimezone)) {
    tzWarning = `Times are displayed in ${venues[0].tz} timezone, your computer is set to ${computerTimezone}.`;
  }

  return (
    <>
      {tzWarning && <Alert color="yellow">{tzWarning}</Alert>}

      <Card
        style={{ marginBottom: "1rem", padding: "1rem", width: "fit-content" }}
      >
        {venues.map((venue) => (
          <React.Fragment key={`venue-roster-supervisor-${venue._id}`}>
            {venues.length > 1 && <p>{venue.name}</p>}
            <RosterSupervisorCollapse
              shiftId={shiftId}
              sessionId={sessionId}
              venueId={venue._id}
              showHideAppointmentFromSheetButton={props.showHideAppointmentFromSheetButton}
            />
          </React.Fragment>
        ))}
        {venues.map((venue) => (
          <React.Fragment key={`venue-roster-administrator-${venue._id}`}>
            {venues.length > 1 && <p>{venue.name}</p>}
            <RosterAdministratorCollapse
              shiftId={shiftId}
              sessionId={sessionId}
              venueId={venue._id}
              showHideAppointmentFromSheetButton={props.showHideAppointmentFromSheetButton}
            />
          </React.Fragment>
        ))}
      </Card>
      <Card style={styles.venueContainer}>
        <table
          style={{
            borderCollapse: "collapse",
            borderSpacing: 0,
            border: "1px solid black",
          }}
        >
          <thead>
            {venues.length > 1 && (
              <tr>
                <th />
                {venues.map((venue) => (
                  <th
                    style={styles.th}
                    key={`venue-header-${venue._id}`}
                    colSpan={venue.courts.length}
                  >
                    {venue.name}
                  </th>
                ))}
              </tr>
            )}
            <tr>
              {/* Time Cell + Courts */}
              <th style={{ ...styles.th, width: "50px" }}>
                <p>Time</p>
              </th>
              {venueCourts.map(({ vid, court }, idx) => (
                <th key={idx} style={{ ...styles.th, width: gameCellWidth }}>
                  <p>{court}</p>
                </th>
              ))}
              <th style={{ ...styles.th, width: "100px" }}>Break</th>
            </tr>
          </thead>
          <tbody style={{ textAlign: "center" }}>
            {slots
              .map((slot, idx) => {
                // For each time (5 minute intervals)

                const hasTime = showExtraPadding || !!slot[0]

                return (<tr key={`${venues[0]._id}-slot-${idx}`}>
                  <td key="slotCell" style={hasTime ? styles.timeCell : {}}>
                    {hasTime && <p>{slot[0]?.setZone(venues[0]?.tz).toFormat("hh:mm a") || ""}</p>}
                  </td>

                  {venues.map((venue) =>
                    venue.courts.map((court, cidx) => {
                      // for each court across

                      const key = `${venue._id}_${court}`;
                      if (!(key in slot[1])) {
                        return <td key={key} style={styles.nogameBorder} />;
                      }
                      const gameInfo = slot[1][key];
                      // this means there is a game in progress, the RosterGameCell returns
                      // a td that is rowspan-d the length / slot length = number of time slots
                      // @ts-ignore
                      const gameId = gameInfo?.id;
                      if (!gameId || typeof gameInfo === "string") {
                        return null;
                      }

                      return (
                        <RosterGameCell
                          key={key}
                          style={{
                            minWidth: "200px",
                            width: gameCellWidth,
                            ...styles.td,
                          }}
                          shiftId={shiftId}
                          sessionId={sessionId}
                          gameId={gameId}
                          hideTeamNames={hideTeamNames}
                          // @ts-ignore
                          tdproperties={{
                            // @ts-ignore
                            rowSpan: gameInfo.length,
                          }}
                          highlightProblemGames={highlightProblemGames}
                          gridView={gridView}
                          showHideAppointmentFromSheetButton={props.showHideAppointmentFromSheetButton}
                        />
                      );
                    }),
                  )}
                  {
                    idx in breakCellMap && (
                      <RefereesBreakCell
                        span={breakCellMap[idx].span}
                        start={breakCellMap[idx].start}
                        finish={breakCellMap[idx].finish}
                        persons={personsOnShift}
                      />
                    )
                    // Type '{ span: any; start: any; finish: any; persons: string[]; }' is missing the following properties
                    // from type '{ persons: string[]; start: DateTime; finish: DateTime; span: number; availabilities:
                    // { [person: string]: IIntervalTree; }; personsMap: { [person: string]: IPerson; }; context?: Context<...> | undefined; store?: Store<...> | undefined; }': availabilities, personsMap
                  }
                </tr>
                )
              })
              .filter((a) => a)}
          </tbody>
        </table>
      </Card>
    </>
  );
};

function mapStateToProps(state: IState, ownProps) {
  return {
    gamesMap: state.games,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    // actions: bindActionCreators(actions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RosterGrid);
